import { CountrySelector } from "@/components/auth/CountrySelector.tsx";
import { landAfterSignIn, type SignInLanding } from "@/components/auth/landAfterSignIn.ts";
import { NameStep } from "@/components/auth/NameStep.tsx";
import { passwordlessOptionsQuery } from "@/components/auth/passwordlessOptions.ts";
import { SmsConsentCheckbox } from "@/components/auth/SmsConsentCheckbox.tsx";
import { VerificationCodeInput } from "@/components/auth/VerificationCodeInput.tsx";
import { OkThxWordmark } from "@/components/icons/Logo.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { api } from "@/lib/http.ts";
import { countryList } from "@/shared/countryList.ts";
import { sendFailureMessage, verifyFailureMessage } from "@/shared/passwordlessAuthCopy.ts";
import {
  applyPhoneEdit,
  defaultCountryName,
  fromE164,
  type PhonePrefill,
  toE164,
} from "@/shared/phoneInput.ts";
import type {
  SendCodeReq,
  SendCodeResp,
  VerifyCodeReq,
  VerifyCodeResp,
} from "@/types/passwordlessAuth.ts";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "wouter";

/**
 * Signing up with a phone number: the number and the consent to text it, then
 * the code that proves the number is theirs.
 *
 * A page of its own rather than a form on the sign-in page, so the number gets
 * the same room the onboarding step gives it, country selector included. The
 * session arrives as a cookie on the verify response, so landing in the app is
 * a full navigation rather than a route change, exactly as the claim landing
 * does it.
 */
export const PhoneAuth = () => {
  const [, setLocation] = useLocation();
  const [params] = useSearchParams();
  // Set by the OAuth callback when a cancelled sign-in was diverted here, so the
  // destination that started the flow survives the change of door.
  const returnTo = params.get("returnTo");
  // Cleared when they ask for a different number, which is the one time the
  // number okthx texted from is the wrong one to offer.
  const [prefill, setPrefill] = useState(readPhonePrefill);
  const options = useQuery(passwordlessOptionsQuery);
  const [sentTo, setSentTo] = useState<string | null>(null);
  // Set only for someone nobody has named yet, which is the one case that has
  // a question left to ask after the session exists.
  const [askName, setAskName] = useState<SignInLanding | null>(null);

  const send = useMutation({
    mutationFn: async (req: SendCodeReq): Promise<SendCodeResp> => {
      const res = await api.post<SendCodeResp>("/api/p/auth/passwordless/send-code", req);
      return res.data;
    },
  });

  const verify = useMutation({
    mutationFn: async (req: VerifyCodeReq): Promise<VerifyCodeResp> => {
      const res = await api.post<VerifyCodeResp>("/api/p/auth/passwordless/verify-code", req);
      return res.data;
    },
    onSuccess: (data) => {
      if (data.status !== "ok") return;
      const landing: SignInLanding = { threadId: data.onboardingThreadId ?? null };
      // The session exists from here on, so the name is asked as a signed-in
      // person rather than collected from whoever typed a number.
      if (data.isNewUser) {
        setAskName(landing);
        return;
      }
      landAfterSignIn(landing, returnTo, "phone");
    },
  });

  // The door can be shut between the sign-in page rendering its button and this
  // page loading, and someone can always type the path.
  const doorShut = !options.isPending && !options.data?.signupEnabled;
  useEffect(() => {
    if (doorShut) setLocation("/auth", { replace: true });
  }, [doorShut, setLocation]);
  if (!options.data?.signupEnabled) return null;

  const step = () => {
    if (askName) {
      return (
        <NameStep
          statsigName="auth-phone-set-name"
          onSaved={() => landAfterSignIn(askName, returnTo, "phone")}
        />
      );
    }
    if (sentTo !== null) {
      return (
        <CodeStep
          phone={sentTo}
          verify={verify}
          onSubmit={(code) => verify.mutate({ phone: sentTo, code, smsConsent: true })}
          onResend={() => send.mutate({ phone: sentTo, smsConsent: true })}
          onStartOver={() => {
            setSentTo(null);
            setPrefill(null);
            verify.reset();
            send.reset();
          }}
        />
      );
    }
    return (
      <NumberStep
        prefill={prefill}
        send={send}
        onSent={setSentTo}
        onBack={() => setLocation("/auth")}
      />
    );
  };

  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 py-10">
      <div className="flex w-full max-w-sm flex-col items-center gap-8">
        <OkThxWordmark className="h-[32px] w-[136px]" />
        {step()}
      </div>
    </div>
  );
};

/**
 * The number okthx's reply to an unrecognized sender carries in the fragment
 * (`#phone=…`), read once and dropped from the address bar before anything
 * renders, so it stays out of history, copied links and the error tracker's
 * record of the page. Null when there is none or it doesn't parse.
 */
const readPhonePrefill = (): PhonePrefill | null => {
  const phone = new URLSearchParams(window.location.hash.slice(1)).get("phone");
  if (phone === null) return null;
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return fromE164(phone, countryList);
};

type SendMutation = ReturnType<typeof useMutation<SendCodeResp, Error, SendCodeReq>>;
type VerifyMutation = ReturnType<typeof useMutation<VerifyCodeResp, Error, VerifyCodeReq>>;

/** The number and the consent that pays for the text. */
const NumberStep = ({
  prefill,
  send,
  onSent,
  onBack,
}: {
  prefill: PhonePrefill | null;
  send: SendMutation;
  onSent: (phone: string) => void;
  onBack: () => void;
}) => {
  // The country lives outside the form because it is not submitted: it decides
  // how the typed digits are grouped and what they mean, and the form's value
  // is the national number it formats.
  const [countryName, setCountryName] = useState(
    () => prefill?.countryName ?? defaultCountryName(countryList),
  );
  const country = countryList.find((c) => c.name === countryName) ?? countryList[0];
  const sendFailure = sendFailureMessage(send.isError, send.data);

  const form = useForm({
    defaultValues: { phoneNumber: prefill?.national ?? "", smsConsent: false },
    onSubmit: async ({ value }) => {
      const e164 = toE164(value.phoneNumber, country);
      if (e164 === null || !value.smsConsent) return;
      const sent = await send.mutateAsync({ phone: e164, smsConsent: value.smsConsent });
      if (sent.status === "sent") onSent(e164);
    },
  });

  return (
    <form
      className="flex w-full flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <div className="flex flex-col gap-2 text-center">
        <h1 className="text-2xl font-semibold">Continue with phone</h1>
        <p className="text-sm text-muted-foreground">
          We will text you a code to confirm your number.
        </p>
      </div>

      <form.Field name="phoneNumber">
        {(field) => (
          <div className="flex flex-col gap-2">
            <Label htmlFor="phone-auth-number">Phone number</Label>
            <div className="border-input focus-within:ring-ring flex overflow-hidden rounded-md border bg-background focus-within:ring-2 focus-within:ring-offset-2">
              <div className="flex items-center border-r border-input py-2">
                <CountrySelector
                  value={countryName}
                  onValueChange={(next) => {
                    setCountryName(next);
                    // Grouping is per numbering plan, so a partially typed
                    // number formatted for the old country is meaningless
                    // under the new one.
                    field.handleChange("");
                  }}
                />
              </div>
              <Input
                id="phone-auth-number"
                type="tel"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) =>
                  field.handleChange(applyPhoneEdit(e.target.value, field.state.value, country))
                }
                placeholder="Phone number"
                autoComplete="tel"
                // The country selector and this field are one control sharing
                // one border, so the input brings no frame of its own.
                className="min-w-0 flex-1 rounded-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          </div>
        )}
      </form.Field>

      <form.Field name="smsConsent">
        {(field) => (
          <SmsConsentCheckbox
            checked={field.state.value}
            onCheckedChange={(checked) => field.handleChange(checked)}
          />
        )}
      </form.Field>

      {sendFailure && <p className="text-sm text-destructive">{sendFailure}</p>}

      {/* The agreement the sign-up records. Terms linked from the consent box
          are terms nobody agreed to; continuing past this line is the act
          `termsAcceptedVia: "signupPage"` claims happened. */}
      <p className="text-xs text-muted-foreground">
        By continuing you agree to our{" "}
        <a
          href="https://okthx.com/terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          terms of service
        </a>{" "}
        and{" "}
        <a
          href="https://okthx.com/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          privacy policy
        </a>
        .
      </p>

      <form.Subscribe
        selector={(state) => ({
          ready: toE164(state.values.phoneNumber, country) !== null && state.values.smsConsent,
          submitting: state.isSubmitting,
        })}
      >
        {({ ready, submitting }) => (
          <Button
            type="submit"
            disabled={!ready || submitting}
            className="w-full"
            data-statsig-name="auth-phone-send-code"
          >
            {submitting ? "Sending..." : "Text me a code"}
          </Button>
        )}
      </form.Subscribe>
      <Button
        type="button"
        variant="ghost"
        onClick={onBack}
        className="w-full"
        data-statsig-name="auth-phone-back"
      >
        Back
      </Button>
    </form>
  );
};

/** The six-digit code, for the number the first step sent to. */
const CodeStep = ({
  phone,
  verify,
  onSubmit,
  onResend,
  onStartOver,
}: {
  phone: string;
  verify: VerifyMutation;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onStartOver: () => void;
}) => {
  // Typing again hides the last failure until the next attempt answers.
  const [cleared, setCleared] = useState(false);
  const failure = cleared ? "" : verifyFailureMessage(verify.isError, verify.data);

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="text-2xl font-semibold">Enter the code</h1>
        <p className="text-sm text-muted-foreground">We texted a code to {phone}.</p>
      </div>
      <VerificationCodeInput
        onComplete={(code) => {
          setCleared(false);
          onSubmit(code);
        }}
        disabled={verify.isPending}
        error={failure}
        onErrorClear={() => setCleared(true)}
      />
      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          onClick={onResend}
          className="w-full"
          data-statsig-name="auth-phone-resend-code"
        >
          Resend code
        </Button>
        <Button
          variant="ghost"
          onClick={onStartOver}
          className="w-full"
          data-statsig-name="auth-phone-change-number"
        >
          Use a different number
        </Button>
      </div>
    </div>
  );
};
